import { createContext, useState, useContext, useEffect } from 'react';
import { useAuth } from './AuthProvider';
import CautelaService from '@/services/CautelaService';
import { useToast } from '@/components/ui/use-toast';
import { labelSingular } from '@/utils/copyTituloBilhete';

interface ICarrinhoContext {
    qtdCarrinho: number;
    carrinhoCautelas: number[];
    addItemCarrinho: (idCautela: number) => void;
    addItemSurpresinha: (qtdSurpresinha: number) => void;
    reservarTitulosCarrinho: () => void;
    limparCarrinho: () => void;
    atualizarQuantidadeCarrinho: () => void;
    removerCautelaCarrinho: (idCautela: number) => void;
}

const CarrinhoContext = createContext<ICarrinhoContext | null>(null);

export const CarrinhoProvider = ({ children }: any) => {
    const [carrinhoCautelas, setCarrinhoCautelas] = useState<number[]>([]);
    const [carrinhoSurpresinha, setCarrinhoSurpresinha] = useState<number>(0);
    const [qtdCarrinho, setQtdCarrinho] = useState<number>(0);
    const { isAuthorized } = useAuth()
    const { toast } = useToast()

    useEffect(() => {
        if (carrinhoCautelas.length || carrinhoSurpresinha) {
            reservarTitulosCarrinho();
        } else {
            atualizarQuantidadeCarrinho();
        }
    }, [isAuthorized])

    useEffect(() => {
        atualizarQuantidadeCarrinho();
    }, [carrinhoCautelas, carrinhoSurpresinha])

    async function atualizarQuantidadeCarrinho() {
        if (isAuthorized) {
            const { Data: result } = await CautelaService.buscarQuantidadeCautelasCarrinho();
            setQtdCarrinho(result.QuantidadeCautelasReservadas)
        } else {
            setQtdCarrinho(carrinhoCautelas.length + carrinhoSurpresinha)
        }
    }

    async function limparCarrinho() {
        try {
            const result = await CautelaService.removerCautelasCarrinho();
            toast({
                variant: 'success',
                description: result.Message,
                duration: 3000
            })
        } catch (error: any) {
            toast({
                variant: 'destructive',
                description: error,
                duration: 4000
            });
        }

        atualizarQuantidadeCarrinho()
    }

    async function addItemCarrinho(idCautela: number) {
        try {
            if (isAuthorized) {
                await CautelaService.adicionarCautelaCarrinho(idCautela);
                atualizarQuantidadeCarrinho();
            } else {
                setCarrinhoCautelas(c => [...c, idCautela])
            }

            toast({
                description: `${labelSingular()} Adicionado ao carrinho`,
                duration: 3000,
                variant: 'success'
            })
        } catch (error: any) {
            toast({
                variant: 'destructive',
                description: error,
                duration: 4000
            });
            throw error;
        }
    }

    async function addItemSurpresinha(qtdSurpresinha: number) {
        if (isAuthorized) {
            try {
                const result = await CautelaService.adicionarCautelasCarrinhoPorQtd(qtdSurpresinha);
                toast({
                    variant: 'success',
                    description: result.Message,
                    duration: 3000
                })
            } catch (error: any) {
                toast({
                    variant: 'destructive',
                    description: error,
                    duration: 4000
                })
                return;
            }
            atualizarQuantidadeCarrinho();
        } else {
            setCarrinhoSurpresinha(c => c + qtdSurpresinha)
        }
    }

    async function reservarTitulosCarrinho() {
        if (carrinhoSurpresinha > 0) {
            const qtdCarrinhoSurpresinha = carrinhoSurpresinha;
            await CautelaService.adicionarCautelasCarrinhoPorQtd(carrinhoSurpresinha).then((result) => {
                toast({
                    variant: result.Data.length === qtdCarrinhoSurpresinha ? 'success' : 'destructive',
                    description: result.Message,
                    duration: 4000
                })
            })
        }

        if (carrinhoCautelas.length > 0) {
            const qtdCarrinhoCautelas = carrinhoCautelas.length;
            await CautelaService.adicionarCautelasCarrinhoPorIdsCautelas(carrinhoCautelas).then((result) => {
                if (result.Message) {
                    toast({
                        variant: result.Data.length === qtdCarrinhoCautelas ? 'success' : 'destructive',
                        description: result.Message,
                        duration: 4000
                    })
                }
            })
        }

        setCarrinhoCautelas([]);
        setCarrinhoSurpresinha(0)
        await atualizarQuantidadeCarrinho();
    }

    async function removerCautelaCarrinho(idCautela: number) {
        try {
            const result = await CautelaService.removerCautelaCarrinho(idCautela)
            toast({
                variant: 'success',
                description: result.Message,
                duration: 3000
            })
        } catch (error: any) {
            toast({
                variant: 'destructive',
                description: error,
                duration: 4000
            });
        }
    }

    return (
        <CarrinhoContext.Provider value={{ addItemCarrinho, removerCautelaCarrinho, addItemSurpresinha, limparCarrinho, atualizarQuantidadeCarrinho, qtdCarrinho, reservarTitulosCarrinho, carrinhoCautelas }}>
            {children}
        </CarrinhoContext.Provider >
    )
}

export function useCarrinho() {
    const context = useContext<ICarrinhoContext | any>(CarrinhoContext);

    return context as ICarrinhoContext;
}